import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShieldAlert, Lock, Mail, Loader2 } from 'lucide-react';
import AuthService from '../services/AuthService';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    try {
      await AuthService.login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4 antialiased dark:bg-ink-950">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-subtle overflow-hidden dark:bg-ink-900 dark:border-neutral-700">
          <div className="px-8 pt-8 pb-6 text-center border-b border-neutral-100 dark:border-neutral-800">
            <img src="/sehaLogo.jpeg" alt="SEHA Logo" className="h-16 mx-auto mb-4 object-contain" />
            <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">CBRNE Command Platform</h1>
            <p className="text-xs text-neutral-400 mt-1 dark:text-neutral-500">SEHA — Al Dhafra Hospitals · Authorized personnel only</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="form-label" htmlFor="login-email">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  className="form-input pl-9"
                  placeholder="you@seha.ae"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="form-label" htmlFor="login-password">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500" />
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  className="form-input pl-9"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-seha-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-seha-600 disabled:opacity-60 transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>

            <p className="text-center text-[11px] text-neutral-400 dark:text-neutral-500">
              Forgot your password? Contact your system administrator.
            </p>
          </form>
        </div>

        <p className="text-center text-[11px] text-neutral-400 mt-4 dark:text-neutral-500">
          Access is monitored and audit-logged in line with hospital cybersecurity policy.
        </p>
      </div>
    </div>
  );
}
